(function (global, factory) {
    typeof exports === 'object' && typeof module !== 'undefined' ? factory(exports) :
    typeof define === 'function' && define.amd ? define(['exports'], factory) :
    (factory((global.ReduxRequestMiddleware = {})));
}(this, (function (exports) { 'use strict';

    /*! *****************************************************************************
    Copyright (c) Microsoft Corporation. All rights reserved.
    Licensed under the Apache License, Version 2.0 (the "License"); you may not use
    this file except in compliance with the License. You may obtain a copy of the
    License at http://www.apache.org/licenses/LICENSE-2.0

    THIS CODE IS PROVIDED ON AN *AS IS* BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
    KIND, EITHER EXPRESS OR IMPLIED, INCLUDING WITHOUT LIMITATION ANY IMPLIED
    WARRANTIES OR CONDITIONS OF TITLE, FITNESS FOR A PARTICULAR PURPOSE,
    MERCHANTABLITY OR NON-INFRINGEMENT.

    See the Apache Version 2.0 License for specific language governing permissions
    and limitations under the License.
    ***************************************************************************** */
    function __rest(s, e) {
      var t = {};

      for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0) t[p] = s[p];

      if (s != null && typeof Object.getOwnPropertySymbols === "function") for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) if (e.indexOf(p[i]) < 0) t[p[i]] = s[p[i]];
      return t;
    }
    function __awaiter(thisArg, _arguments, P, generator) {
      return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) {
          try {
            step(generator.next(value));
          } catch (e) {
            reject(e);
          }
        }

        function rejected(value) {
          try {
            step(generator["throw"](value));
          } catch (e) {
            reject(e);
          }
        }

        function step(result) {
          result.done ? resolve(result.value) : new P(function (resolve) {
            resolve(result.value);
          }).then(fulfilled, rejected);
        }

        step((generator = generator.apply(thisArg, _arguments || [])).next());
      });
    }
    function __await(v) {
      return this instanceof __await ? (this.v = v, this) : new __await(v);
    }
    function __asyncGenerator(thisArg, _arguments, generator) {
      if (!Symbol.asyncIterator) throw new TypeError("Symbol.asyncIterator is not defined.");
      var g = generator.apply(thisArg, _arguments || []),
          i,
          q = [];
      return i = {}, verb("next"), verb("throw"), verb("return"), i[Symbol.asyncIterator] = function () {
        return this;
      }, i;

      function verb(n) {
        if (g[n]) i[n] = function (v) {
          return new Promise(function (a, b) {
            q.push([n, v, a, b]) > 1 || resume(n, v);
          });
        };
      }

      function resume(n, v) {
        try {
          step(g[n](v));
        } catch (e) {
          settle(q[0][3], e);
        }
      }

      function step(r) {
        r.value instanceof __await ? Promise.resolve(r.value.v).then(fulfill, reject) : settle(q[0][2], r);
      }

      function fulfill(value) {
        resume("next", value);
      }

      function reject(value) {
        resume("throw", value);
      }

      function settle(f, v) {
        if (f(v), q.shift(), q.length) resume(q[0][0], q[0][1]);
      }
    }

    /**
     * @param { Promise } promise
     * @param { Object= } errorExt - Additional Information you can pass to the err object
     * @return { Promise }
     */
    function to(promise, errorExt) {
      return promise.then(function (data) {
        return [null, data];
      }).catch(function (err) {
        if (errorExt) {
          Object.assign(err, errorExt);
        }

        return [err, undefined];
      });
    }

    const REQUEST = Symbol("@@request");
    const PENDING = Symbol("@@pending");
    const CANCELLED = Symbol("@@cancelled");
    const SETTLED = Symbol("@@settled");
    const REJECTED = Symbol("@@rejected");
    const FULFILLED = Symbol("@@fulfilled");
    const ABORT = Symbol("@@abort");

    var symbols = /*#__PURE__*/Object.freeze({
        REQUEST: REQUEST,
        PENDING: PENDING,
        CANCELLED: CANCELLED,
        SETTLED: SETTLED,
        REJECTED: REJECTED,
        FULFILLED: FULFILLED,
        ABORT: ABORT
    });

    /* taken from https://gist.github.com/jed/982883 */function b(c// placeholder
    ){return c// if the placeholder was passed, return
    ?// a random number from 0 to 15
    (c^// unless b is 8,
    // in which case
    16*Math.random()>>// a random number from
    c/4).// 8 to 11
    toString(16)// in hexadecimal
    :// or otherwise a concatenated string:
    "10000000-1000-4000-8000-100000000000".// -100000000000,
    replace(// replacing
    /[018]/g,// zeroes, ones, and eights with
    b// random hex digits
    )}

    class SimpleCache {
        constructor(options, cancel) {
            this._cache = new Map();
            this._options = options;
        }
        get cache() {
            return this._cache;
        }
        set options(options) {
            this._options = options;
        }
        /**
         *  Clear the request cache either by a namespaced requestType or the uid of the request
         *
         * @param {object} options - options object specifying the location and the data to be stored in the cache
         * @param {string} uid     - unique identifier of the specific request
         * @returns                - void
         */
        cacheRequest({ namespace, concurrent }, cancel) {
            const uid = b();
            const requestType = this.getNamespace(namespace, uid) || SimpleCache.generic;
            // the requestType is used as a top level mechanism to define which api endpoint is being hit
            // @ts-ignore
            const existingCache = this._cache.get(requestType);
            const requestStatus = {
                requestedAt: Date.now(),
                status: PENDING,
                cancel,
                uid
            };
            // if there is not a request pending for this request type or this request type allows concurrent requests
            // then add the request data to the cache at that specific request type namespace
            if (!existingCache) {
                const requestTypeMap = new Map().set(uid, requestStatus);
                // ts not allowing a symbol as an indexer
                // @ts-ignore
                this._cache.set(requestType, requestTypeMap);
            }
            else {
                // requests that have no origin and use the default @@generic type are always allowed to
                // cache multiple requests
                // Those that are namespaced can allow or disallow requests to made multiple times against the same endpoint
                // in parallel or not.
                if (requestType !== SimpleCache.generic) {
                    if (concurrent !== undefined && !concurrent) {
                        // concurrent request of the same type are not allowed so either cancel existing
                        // or simply ignore this cache request and allow the first to take precedence
                        // TODO allow for more advanced options, right now simply cancel request that are still pending
                        this.markCacheValueAsCancelled(existingCache);
                        if (cancel) {
                            existingCache.forEach(value => value.cancel("request made against same namespace, cancelling previous request"));
                        }
                    }
                }
                // cache most recent request
                existingCache.set(uid, requestStatus);
            }
            return requestStatus;
        }
        /**
         *  Clear the request cache either by a namespaced requestType or the default namespace of @@generic
         *
         * @param {object} options - options object specifying the location in the cache that needs to be cleared
         * @param {string} uid     - unique identifier of the specific request
         * @returns                - void
         */
        clearRequest({ namespace }, uid) {
            const requestType = this.getNamespace(namespace, uid) || SimpleCache.generic;
            // @ts-ignore
            const currentRequest = this._cache.get(requestType);
            // clear request cache once request is complete
            if (currentRequest) {
                currentRequest.delete(uid);
                // clear the request cache at the request type if there are no request left of the given type
                if (currentRequest.size === 0) {
                    // @ts-ignore
                    this._cache.delete(requestType);
                }
            }
        }
        cancelAllRequests() {
            this._cache.forEach((value, key) => {
                value.forEach((v, k) => {
                    if (v.cancel) {
                        v.cancel("request made against same namespace, cancelling previous request");
                    }
                });
            });
            this.cleanCache();
        }
        /**
         *  Clear the entire cache
         *
         * @returns - void
         */
        cleanCache() {
            this._cache.clear();
        }
        /**
         *  Get current request status
         *
         * @param {any} requestType - key used to namespace the request type
         * @param {string} uid      - unique id of the specific request
         * @returns - symbol
         */
        getRequestStatus(requestType, uid) {
            const namespace = this.getNamespace(requestType, uid);
            // @ts-ignore
            return this._cache.get(namespace).get(uid).status;
        }
        /**
         *  Cancel request
         *
         * @returns - void
         */
        markCacheValueAsCancelled(cache) {
            for (let value of cache.values()) {
                value.status = CANCELLED;
            }
        }
        getNamespace(namespace, uid) {
            return namespace
                ? typeof namespace === "function"
                    ? namespace(this._options, uid)
                    : namespace
                : SimpleCache.generic;
        }
    }
    SimpleCache.generic = Symbol("@@generic");

    /**
     * Function simply wraps a generator and calls that generator's next method each time it's executed
     *
     */
    function tick(generator) {
        return function next() {
            return __awaiter(this, void 0, void 0, function* () {
                return generator.next();
            });
        };
    }
    /**
     * Function that returns a Promise that resolves after a specified amount of time
     *
     * @param {number} amount - amount of time to wait until resolving the promise
     * @returns {} - Promise
     */
    function delay(amount) {
        return __awaiter(this, void 0, void 0, function* () {
            return new Promise(resolve => {
                setTimeout(resolve, amount);
            });
        });
    }
    /**
     * Async generator function that calls a provided endpoint and continues to yield the response
     * from the given endpoint until the yieldUntilCondition callback returns true.
     *
     * @param {function} endpoint - function that returns a Promise
     * @param {function} yieldUntilCondition - callback that receives the fetch response and returns a boolean
     * @returns {} - Promise
     */
    function fetch(endpoint, yieldUntilCondition) {
        return __asyncGenerator(this, arguments, function* fetch_1() {
            while (true) {
                // call the intended api
                const [err, response] = yield __await(to(endpoint()));
                // throw on error from ajax request
                if (err) {
                    throw err;
                }
                // break out of the iterator when the condition is met
                if (yieldUntilCondition(response)) {
                    return yield __await(response);
                }
                // else simply yield the value so we can continue to poll
                yield yield __await(response);
            }
        });
    }
    /**
     * Function that polls a generator's next() method until a provided callback's condition has been met.
     *
     * @param {function} condition - function that true or false based on the condition being tested
     * @param {endpoint} endpoint - function that when executes calls an async api endpoint and returns the promise that resolves with the error or response from the service
     * @param {number} pollInterval - time between polling the api
     * @param {number} timeout - time to bail if the condition callback is never met
     * @returns {any} - response from the api
     */
    function poll(endpoint, { pollUntil, pollInterval = 5000, timeout = 1000 * 60 * 2 }) {
        return __awaiter(this, void 0, void 0, function* () {
            let gen = fetch(endpoint, pollUntil);
            let timesUp = false;
            let response = yield tick(gen)();
            if (typeof timeout === "number" && !Number.isNaN(timeout)) {
                setTimeout(() => (timesUp = true), timeout);
            }
            while (!response.done && !timesUp) {
                response = yield delay(pollInterval).then(tick(gen));
            }
            // if the response has yet to resolve after polling for a duration that exceeds the
            // specified timeout then throw an error that matches the
            if (timesUp) {
                throw { response: { status: 418, statusText: "request timeout" } };
            }
            return response.value;
        });
    }

    const axios = require("axios");
    let requestCache = new SimpleCache();
    /**
     * Take the current state of the application and returns a function that receives the Dispatcher
     * which then returns another function which receives the action. Any action that is dispatched with
     * the symbol `Symbols.REQUEST` will be picked up and handled by the function.
     *
     * If any of the lifecycle methods are configured for the request, namely PENDING, SETTLED, REJECTED or FULFILLED
     * then each lifecycle can either return a flux standard payload or alternatively they can be a function that
     * can perform some side-effects and ultimately return a flux standard action that will eventually be dispatched.
     *
     * Each request that is made is given a uuid and is cached using the SimpleCache cache. Requests that are given
     * a namespace are cached against that namespace and can be used to handle concurrent requests.
     *
     * @param {object} store - database object paths reference to perform the query
     * @returns {function(): function(): Promise<any>} - Promise
     */
    const requestMiddleware = (store) => (next) => (action) => {
        if (action.type === REQUEST) {
            const { instance, lifecycle = {}, options, poll: pollOptions, fetch } = action.payload;
            if (!options && !fetch) {
                throw new Error(`Options is required when the fetch param is not supplied, either none were provided or they do not match the correct signature.
         To find out what options are supported see: https://github.com/axios/axios`);
            }
            let source = null;
            let reqConfig = null;
            if (options) {
                source = axios.CancelToken.source();
                // allow for the config to be a function that receives the application state before returning
                // the axios configuration for the request(s)
                reqConfig =
                    typeof options === "function" ? options(store.getState()) : options;
                // store the current request configuration for use with forming the request namespace
                requestCache.options = reqConfig;
            }
            // cache the current request
            const r = requestCache.cacheRequest(action.payload, source && source.cancel);
            const uid = r.uid;
            // if pending lifecycle is configured then create a request id that is used to cache this specific request
            if (lifecycle[PENDING]) {
                next(formPayload(lifecycle[PENDING], uid, action, store.getState()));
            }
            return new Promise((resolve, reject) => __awaiter(undefined, void 0, void 0, function* () {
                // set the cancel token for each request and alternatively use the
                // axios instance, which configures the request defaults that are merged
                // with each configuration object when the async call is made
                let request;
                if (fetch) {
                    request = () => fetch(action, store.getState());
                }
                else {
                    request = () => Array.isArray(reqConfig)
                        ? axios.all(reqConfig.map(o => (instance || axios)(Object.assign({}, o, { cancelToken: source.token }))))
                        : (instance || axios)(Object.assign({}, reqConfig, { cancelToken: source.token }));
                }
                // make request(s)
                const [err, response] = yield to(pollOptions ? poll(request, pollOptions) : request());
                if (lifecycle[SETTLED] ||
                    lifecycle[FULFILLED] ||
                    lifecycle[REJECTED]) {
                    // dispatch actions for success or error once the response(s) have been settled and the
                    // response was not cancelled at some point during its lifespan
                    const settledValue = fetch
                        ? err || response
                        : err
                            ? err.response
                            : response;
                    if (!axios.isCancel(err) || fetch) {
                        const lifecycleInterceptor = err
                            ? lifecycle[REJECTED]
                            : lifecycle[FULFILLED];
                        // if a fulfilled handler or error handler was provided
                        // then call the respective handler
                        if (lifecycleInterceptor) {
                            // call the next middleware
                            next(onComplete(action, store, uid, lifecycleInterceptor, settledValue));
                        }
                        // in addition call the settled handler regardless if it was provided
                        if (lifecycle[SETTLED]) {
                            next(formPayload(lifecycle[SETTLED], uid, action, store.getState(), settledValue));
                        }
                        else if (!lifecycleInterceptor) {
                            // else no handler was provided for the success and/or error scenario
                            // and no settled handler so simply resolve or reject with the response
                            !err ? resolve(response) : reject(err);
                        }
                    }
                    else {
                        next(formPayload(lifecycle[CANCELLED] || { type: CANCELLED }, uid, action, store.getState(), null));
                    }
                    resolve(settledValue);
                }
                else {
                    // when no lifecycle handlers have been specified then we simply resolve or reject
                    // and the code that dispatched the action can handle it without the using the middleware
                    !err ? resolve(response) : reject(err);
                }
                // clear the cache for the response
                requestCache.clearRequest(action.payload, uid);
            }));
        }
        else if (action.type === ABORT) {
            requestCache.cancelAllRequests();
        }
        else {
            return next(action);
        }
    };
    /**
     * Function take the lifecycle action and normalizes it so that each action's type and payload
     * are consistent for each lifecycle hook
     *
     * @param {object} lifecycle - flux action to be dispatched
     * @param {string} uid       - request identifier
     * @param {object} action    - original action that was dispatched to the request middleware
     * @param {object} state     - application state of the redux store
     * @param {object} response  - either the error or success result of the async request
     * @returns {object}         - action used to be dispatched to the next middleware
     */
    const formPayload = (lifecycle, uid, action, state, response) => {
        const rest = __rest(lifecycle, ["payload"]);
        const payload = typeof lifecycle.payload === "function"
            ? lifecycle.payload(response, action, state)
            : lifecycle.payload;
        return Object.assign({}, Object.assign({}, rest, (payload ? { payload } : {}), { requestId: uid }));
    };
    /**
     * Function that is called when either the SETTLED or REJECTED or FULFILLED lifecycle hooks are called.
     * The function returns the payload and type object that is dispatched when each stage of the lifecycle
     * is reached. The payload configured can return any static data or it can optionally be a function
     * that can operate on the response of the async request before being dispatched.
     *
     * @param {object} action    - original action that was dispatched to the request middleware
     * @param {object} store     - application state of the redux store
     * @param {string} uid       - request identifier
     * @param {object} lifecycle - object used to decorate the action that will eventually be dispatched
     * @param {object} response  - either the error or success result of the async request
     * @returns {object}         - action used to be dispatched to the next middleware
     */
    const onComplete = (action, store, uid, lifecycle, response) => {
        const { statusCodes = new Map() } = action.payload;
        const statCodes = statusCodes.keys();
        if (statCodes && response && response.status) {
            // check for messaging or status code handlers
            for (const codes of statCodes) {
                if (codes.includes(response.status)) {
                    // each code may either return a payload to be dispatched or a function
                    // that is executed and returns a payload
                    return formPayload(statusCodes.get(codes) || {}, uid, action, store.getState(), response);
                }
            }
        }
        return formPayload(lifecycle, uid, action, store.getState(), response);
    };

    exports.symbols = symbols;
    exports.RequestReduxMiddleware = requestMiddleware;

    Object.defineProperty(exports, '__esModule', { value: true });

})));
